// NewItemPanel — 신규 항목 작성 슬라이드 패널 (UX v2 polish).
//
// URL ?new={type} 파라미터가 있을 때 열린다.
// 2026-10-02: 데스크톱(≥ 1024px)에서는 DetailPanel과 같은 Layout 우측 고정 컬럼(팝업 X).
//   768~1023px은 우측 슬라이드 + 딤, 모바일은 풀스크린.
//
// 닫기: URL ?new 파라미터 제거. Esc·X 버튼·(오버레이 모드) 딤 클릭. 외부 클릭 닫기는 제거.
// 브라우저 저장 비밀번호 자동완성 차단: 민감 필드 autoComplete="new-password", 그 외 "off".
// 저장: useItemMutations.createItem 호출 → 성공 시 패널 닫기 + 생성된 item DetailPanel 열기.
//
// 타입별 필드: constants/itemFields.ts (서버 스키마와 같은 키·형식). 초기값은 빈 문자열.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  KeyRound,
  Code2,
  Server,
  FileCode2,
  ShieldCheck,
  HardDrive,
  Globe,
  Layers,
  CreditCard,
  FileText,
  CheckSquare,
  StickyNote,
  X,
} from 'lucide-react';
import { useItemMutations } from '../hooks/useItems';
import { useGroupsTree } from '../hooks/useGroupsTree';
import { useToast } from './Toast';
import { useDetailPanel } from '../hooks/useDetailPanel';
import { formatApiError } from '../api/client';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { ItemFieldInput } from './ItemFieldInput';
import { ITEM_FIELDS, buildPayload } from '../constants/itemFields';
import type { ItemType } from '../api/items';
import type { GroupTreeNode } from '../api/groupsTree';
import { SHORTCUT_EVENT_CLOSE_OVERLAY } from '../hooks/useGlobalShortcuts';

// ─── 타입 레이블 맵 ───────────────────────────────────────────

/** 12종 타입의 한국어 이름 */
const TYPE_LABELS: Record<ItemType, string> = {
  credential_account: '계정',
  credential_api_key: 'API 키',
  credential_server: '서버 크리덴셜',
  credential_env: '환경변수',
  credential_auth: '인증',
  server: '서버 인벤토리',
  domain: '도메인',
  service: '서비스',
  subscription: '구독·라이선스',
  file: '파일',
  checklist: '체크리스트',
  note: '메모',
};

/** 타입별 아이콘 */
const TYPE_ICONS: Record<ItemType, React.ReactNode> = {
  credential_account: <KeyRound size={16} />,
  credential_api_key: <Code2 size={16} />,
  credential_server: <Server size={16} />,
  credential_env: <FileCode2 size={16} />,
  credential_auth: <ShieldCheck size={16} />,
  server: <HardDrive size={16} />,
  domain: <Globe size={16} />,
  service: <Layers size={16} />,
  subscription: <CreditCard size={16} />,
  file: <FileText size={16} />,
  checklist: <CheckSquare size={16} />,
  note: <StickyNote size={16} />,
};

// 타입별 필드 정의는 constants/itemFields.ts (서버 itemTypes.ts와 1:1) 를 사용한다.

// ─── 그룹 트리 → 평면 옵션 목록 변환 헬퍼 ────────────────────

export function flattenGroups(
  nodes: GroupTreeNode[],
  depth = 0,
): Array<{ id: number; label: string }> {
  const result: Array<{ id: number; label: string }> = [];
  for (const node of nodes) {
    const prefix = depth > 0 ? '  '.repeat(depth) + '└ ' : '';
    result.push({ id: node.id, label: prefix + node.name });
    if (node.children.length > 0) {
      result.push(...flattenGroups(node.children, depth + 1));
    }
  }
  return result;
}

// ─── 내부 폼 컴포넌트 ──────────────────────────────────────────

interface NewItemFormProps {
  itemType: ItemType;
  /** 현재 URL에 있는 그룹 ID (기본값으로 사용) */
  defaultGroupId: number | null;
  onClose: () => void;
}

/**
 * 신규 항목 작성 폼.
 * 이름 + 그룹 선택 + 태그 입력 + 타입별 payload 필드.
 */
function NewItemForm({ itemType, defaultGroupId, onClose }: NewItemFormProps) {
  const { createItem } = useItemMutations();
  const toast = useToast();
  const { open: openDetailPanel } = useDetailPanel();

  // 그룹 트리 (credential 전체 불러오기)
  const { data: groupTree = [] } = useGroupsTree('credential');
  const groupOptions = flattenGroups(groupTree);

  // 폼 상태
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState<string>(
    defaultGroupId !== null ? String(defaultGroupId) : '',
  );
  const [tagsInput, setTagsInput] = useState('');
  const [payload, setPayload] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fields = ITEM_FIELDS[itemType] ?? [];

  // payload 필드 변경
  const handlePayloadChange = useCallback((key: string, value: string) => {
    setPayload((prev) => ({ ...prev, [key]: value }));
    // 입력 시 에러 클리어
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // 저장 — 필수·형식 검사 후 서버 형식(number·boolean·string[])으로 변환
  const handleSave = useCallback(async () => {
    const built = buildPayload(fields, payload);
    const errs = { ...built.errors };
    if (!name.trim()) errs['__name'] = '이름은 필수입니다';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    try {
      const created = await createItem.mutateAsync({
        type: itemType,
        name: name.trim(),
        // 미분류(빈 값)면 group_id 자체를 보내지 않는다 — 서버 스키마는 null 비허용이었음
        ...(groupId ? { group_id: Number(groupId) } : {}),
        tags,
        payload: built.payload,
      });
      toast.success(`"${name.trim()}" 항목이 추가되었습니다`);
      onClose();
      // 생성된 항목의 DetailPanel 자동 열기
      openDetailPanel(created.id);
    } catch (err) {
      toast.error(formatApiError(err, '항목 생성 실패'));
    }
  }, [fields, tagsInput, createItem, itemType, name, groupId, payload, toast, onClose, openDetailPanel]);

  // Enter 키 저장 (textarea 제외)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && e.target instanceof HTMLInputElement) {
        e.preventDefault();
        void handleSave();
      }
    },
    [handleSave],
  );

  return (
    <div className="new-item-form" onKeyDown={handleKeyDown}>
      {/* 이름 필드 */}
      <div className="new-item-form__field">
        <Input
          label="이름"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors((prev) => { const n = { ...prev }; delete n['__name']; return n; });
          }}
          placeholder={`${TYPE_LABELS[itemType]} 이름 입력`}
          error={errors['__name']}
          autoComplete="off"
          autoFocus
        />
      </div>

      {/* 그룹 선택 */}
      <div className="new-item-form__field">
        <Select
          label="그룹"
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
        >
          <option value="">그룹 없음 (미분류)</option>
          {groupOptions.map((g) => (
            <option key={g.id} value={String(g.id)}>
              {g.label}
            </option>
          ))}
        </Select>
      </div>

      {/* 타입별 payload 필드 */}
      {fields.map((field) => (
        <div key={field.key} className="new-item-form__field">
          <ItemFieldInput
            field={field}
            value={payload[field.key] ?? ''}
            onChange={(v) => handlePayloadChange(field.key, v)}
            error={errors[field.key]}
            idPrefix="new"
          />
        </div>
      ))}

      {/* 태그 입력 */}
      <div className="new-item-form__field">
        <Input
          label="태그 (쉼표로 구분)"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          placeholder="태그1, 태그2, ..."
        />
      </div>

      {/* 액션 버튼 */}
      <div className="new-item-form__actions">
        <Button variant="ghost" size="sm" onClick={onClose}>
          취소
        </Button>
        <Button
          variant="primary"
          size="sm"
          loading={createItem.isPending}
          onClick={() => void handleSave()}
        >
          추가
        </Button>
      </div>
    </div>
  );
}

// ─── 메인 컴포넌트 ─────────────────────────────────────────────

/**
 * NewItemPanel — URL ?new={type} 파라미터 기반 신규 작성 패널.
 * Layout 우측 컬럼에서 마운트된다 (DetailPanel 자리 대신).
 */
export function NewItemPanel({ panelWidth }: { panelWidth?: number } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const newTypeParam = searchParams.get('new') as ItemType | null;
  const isOpen = newTypeParam !== null;

  // 그룹 ID (기본값용)
  const groupIdParam = searchParams.get('group');
  const defaultGroupId = groupIdParam ? Number(groupIdParam) : null;

  // 패널 닫기
  const handleClose = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('new');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Esc·locker:close-overlay 처리
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    const handleCloseEvent = () => handleClose();
    document.addEventListener('keydown', handleKey);
    window.addEventListener(SHORTCUT_EVENT_CLOSE_OVERLAY, handleCloseEvent);
    return () => {
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener(SHORTCUT_EVENT_CLOSE_OVERLAY, handleCloseEvent);
    };
  }, [isOpen, handleClose]);

  // 외부 클릭 닫기는 제거 (2026-10-02) — 고정 컬럼에서 패널 밖 클릭으로 입력 중 폼이 사라지던 문제.

  // 타입이 유효한 ItemType인지 검증
  const VALID_TYPES: ItemType[] = [
    'credential_account', 'credential_api_key', 'credential_server',
    'credential_env', 'credential_auth', 'note', 'server', 'domain',
    'service', 'subscription', 'file', 'checklist',
  ];
  const validType = newTypeParam !== null && VALID_TYPES.includes(newTypeParam)
    ? newTypeParam
    : null;

  return (
    <>
      {/* 배경 딤처리 */}
      {isOpen && (
        <div
          className="new-item-panel__backdrop"
          aria-hidden="true"
          onClick={handleClose}
        />
      )}

      {/* 패널 본체 */}
      <div
        className={`new-item-panel${isOpen ? ' new-item-panel--open' : ''}`}
        style={panelWidth ? ({ '--panel-width': `${panelWidth}px` } as React.CSSProperties) : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={validType ? `새 ${TYPE_LABELS[validType]} 추가` : '새 항목 추가'}
        aria-hidden={!isOpen}
        data-testid="new-item-panel"
      >
        {isOpen && validType !== null && (
          <>
            {/* 헤더 */}
            <div className="new-item-panel__header">
              <div className="new-item-panel__title">
                <span className="new-item-panel__type-icon" aria-hidden="true">
                  {TYPE_ICONS[validType]}
                </span>
                <h2 className="new-item-panel__title-text">
                  새 {TYPE_LABELS[validType]}
                </h2>
              </div>
              <button
                type="button"
                className="new-item-panel__close"
                onClick={handleClose}
                aria-label="닫기"
              >
                <X size={16} />
              </button>
            </div>

            {/* 스크롤 영역 */}
            <div className="new-item-panel__body">
              <NewItemForm
                itemType={validType}
                defaultGroupId={defaultGroupId}
                onClose={handleClose}
              />
            </div>
          </>
        )}

        {/* 유효하지 않은 타입 파라미터 처리 */}
        {isOpen && validType === null && (
          <div className="new-item-panel__header">
            <p className="muted">알 수 없는 타입입니다.</p>
            <button type="button" onClick={handleClose}>닫기</button>
          </div>
        )}
      </div>
    </>
  );
}
